import { useClientEmployeesQuery } from "@graphql/client-employees/clientEmployees.g";
import {
  TenantedClientApplicationManagedByChoices,
  TenantedClientApplicationReviewStateChoices,
  TenantedClientApplicationSupportedByChoices,
} from "@graphql/generated/graphql.g";
import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { DateRangePicker } from "@design-system/DateRangePicker/DateRangePicker";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { TextField } from "@design-system/TextField/TextField";
import { InfiniteCategorySelect } from "@components/Selects/InfiniteCategorySelect";

type DateRange = [Date | null, Date | null];

// Helper function to convert date range to form values
const dateRangeToFormValues = (
  range: DateRange,
): { createdAt_Gte?: string; createdAt_Lte?: string } => {
  const [startDate, endDate] = range;
  return {
    createdAt_Gte: startDate ? startDate.toISOString() : undefined,
    createdAt_Lte: endDate ? endDate.toISOString() : undefined,
  };
};

// Helper function to convert form values to date range
const formValuesToDateRange = (
  createdAt_Gte?: string,
  createdAt_Lte?: string,
): DateRange => {
  return [
    createdAt_Gte ? new Date(createdAt_Gte) : null,
    createdAt_Lte ? new Date(createdAt_Lte) : null,
  ];
};

export type ClientApplicationFilterFormValues = {
  reviewState?: TenantedClientApplicationReviewStateChoices[] | undefined;
  managedBy?: TenantedClientApplicationManagedByChoices[] | undefined;
  businessOwnerId?: string | undefined;
  supportedBy?: TenantedClientApplicationSupportedByChoices[] | undefined;
  productNameSearch?: string | undefined;
  parentCategoryId?: string | undefined;
  createdAt_Gte?: string | undefined;
  createdAt_Lte?: string | undefined;
  isVisibleFilter?: "all" | "visible" | "hidden" | undefined;
};

const isVisibleFilterOptions = [
  { id: "visible", label: "Visible Only" },
  { id: "hidden", label: "Hidden Only" },
  { id: "all", label: "All Applications" },
];

export type ClientApplicationFilterFormRef = {
  getCurrentValues: () => ClientApplicationFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<ClientApplicationFilterFormValues>;
  values: ClientApplicationFilterFormValues;
  clientId: string;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
  hideBusinessOwner?: boolean;
};

export const ClientApplicationFilterForm = forwardRef<
  ClientApplicationFilterFormRef,
  Props
>(({ onFilterChange, values, clientId, errorsAPI, hideBusinessOwner }, ref) => {
  const { handleSubmit, setValue, watch, getValues } =
    useForm<ClientApplicationFilterFormValues>({
      values: {
        reviewState: values.reviewState ?? [],
        managedBy: values.managedBy ?? [],
        supportedBy: values.supportedBy ?? [],
        businessOwnerId: values.businessOwnerId ?? undefined,
        productNameSearch: values.productNameSearch ?? undefined,
        parentCategoryId: values.parentCategoryId ?? undefined,
        createdAt_Gte: values.createdAt_Gte ?? undefined,
        createdAt_Lte: values.createdAt_Lte ?? undefined,
        isVisibleFilter: values.isVisibleFilter ?? "visible",
      },
    });

  const APPROVAL_OPTIONS: Array<{
    id: TenantedClientApplicationReviewStateChoices;
    label: string;
  }> = [
    {
      id: TenantedClientApplicationReviewStateChoices.Discovered,
      label: `Discovered`,
    },
    {
      id: TenantedClientApplicationReviewStateChoices.Evaluating,
      label: `Being evaluated`,
    },
    {
      id: TenantedClientApplicationReviewStateChoices.Approved,
      label: `Approved`,
    },
    {
      id: TenantedClientApplicationReviewStateChoices.Unapproved,
      label: `Not approved`,
    },
  ];

  const SUPPORTED_BY_OPTIONS: Array<{
    id: TenantedClientApplicationSupportedByChoices;
    label: string;
  }> = [
    {
      id: TenantedClientApplicationSupportedByChoices.Msp,
      label: `MSP`,
    },
    {
      id: TenantedClientApplicationSupportedByChoices.Vendor,
      label: `Vendor`,
    },
    {
      id: TenantedClientApplicationSupportedByChoices.Client,
      label: `Client`,
    },
  ];

  const MANAGED_BY_OPTIONS: Array<{
    id: TenantedClientApplicationManagedByChoices;
    label: string;
  }> = [
    {
      id: TenantedClientApplicationManagedByChoices.Msp,
      label: `MSP`,
    },
    {
      id: TenantedClientApplicationManagedByChoices.Vendor,
      label: `Vendor`,
    },
    {
      id: TenantedClientApplicationManagedByChoices.Client,
      label: `Client`,
    },
  ];

  const clientEmployeesQuery = useClientEmployeesQuery({
    clientId: clientId,
    orderBy: "name",
  });

  const CLIENT_EMPLOYEE_OPTIONS = useMemo(() => {
    const employees =
      clientEmployeesQuery.data?.clientEmployees?.edges
        ?.map((edge) => edge?.node)
        ?.filter((clientEmployee) => !!clientEmployee)
        ?.map((employee) => ({
          id: employee.id,
          label: `${employee.name} (${employee.email})`,
        })) || [];

    return [{ id: "", label: "Not selected" }, ...employees];
  }, [clientEmployeesQuery.data]);

  const reviewState = watch("reviewState");
  const managedBy = watch("managedBy");
  const supportedBy = watch("supportedBy");
  const businessOwnerId = watch("businessOwnerId");
  const productNameSearch = watch("productNameSearch");
  const parentCategoryId = watch("parentCategoryId");
  const createdAt_Gte = watch("createdAt_Gte");
  const createdAt_Lte = watch("createdAt_Lte");
  const isVisibleFilter = watch("isVisibleFilter");

  // Handle date range changes
  const handleDateRangeChange = (range: DateRange) => {
    const { createdAt_Gte, createdAt_Lte } = dateRangeToFormValues(range);
    setValue("createdAt_Gte", createdAt_Gte, { shouldDirty: true });
    setValue("createdAt_Lte", createdAt_Lte, { shouldDirty: true });
  };

  // Expose getCurrentValues method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
    }),
    [getValues],
  );

  const createHandleChange = (
    fieldName: keyof ClientApplicationFilterFormValues,
  ) => {
    return (value: string | string[]) => {
      if (fieldName === "reviewState") {
        setValue(
          fieldName,
          value as TenantedClientApplicationReviewStateChoices[],
          { shouldDirty: true },
        );
      } else if (fieldName === "managedBy") {
        setValue(
          fieldName,
          value as TenantedClientApplicationManagedByChoices[],
          { shouldDirty: true },
        );
      } else if (fieldName === "supportedBy") {
        setValue(
          fieldName,
          value as TenantedClientApplicationSupportedByChoices[],
          { shouldDirty: true },
        );
      } else if (fieldName === "parentCategoryId") {
        const finalValue = Array.isArray(value) ? value[0] : value;
        setValue(fieldName, finalValue === "" ? undefined : finalValue, {
          shouldDirty: true,
        });
      } else if (
        fieldName === "createdAt_Gte" ||
        fieldName === "createdAt_Lte"
      ) {
        const finalValue = Array.isArray(value) ? value[0] : value;
        setValue(fieldName, finalValue === "" ? undefined : finalValue, {
          shouldDirty: true,
        });
      } else {
        // For other fields, handle as single values
        const enumValue = Array.isArray(value) ? value[0] : value;
        const finalValue = enumValue === "" ? undefined : enumValue;
        setValue(fieldName, finalValue as never, { shouldDirty: true });
      }
    };
  };

  return (
    <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
      <FieldGroup label="Application name">
        <TextField
          placeholder="Search by application name..."
          value={productNameSearch ?? ""}
          onChange={(e) =>
            setValue("productNameSearch", e.target.value || undefined, {
              shouldDirty: true,
            })
          }
        />
      </FieldGroup>

      <FieldGroup label="Category">
        <InfiniteCategorySelect
          value={parentCategoryId}
          onChange={(value) => setValue("parentCategoryId", value, { shouldDirty: true })}
          placeholder="Search and select category..."
        />
      </FieldGroup>

      <FieldGroup label="Discovered between">
        <DateRangePicker
          fullWidth
          value={formValuesToDateRange(createdAt_Gte, createdAt_Lte)}
          onChange={handleDateRangeChange}
          placeholder="Select date range"
        />
      </FieldGroup>

      <FieldGroup label="Review state">
        <Select
          options={APPROVAL_OPTIONS}
          value={reviewState ?? []}
          onChange={createHandleChange("reviewState")}
        />
      </FieldGroup>

      <FieldGroup label="Managed by">
        <Select
          options={MANAGED_BY_OPTIONS}
          value={managedBy ?? []}
          onChange={createHandleChange("managedBy")}
        />
      </FieldGroup>

      <FieldGroup label="Supported by">
        <Select
          options={SUPPORTED_BY_OPTIONS}
          value={supportedBy ?? []}
          onChange={createHandleChange("supportedBy")}
        />
      </FieldGroup>

      {!hideBusinessOwner && (
        <FieldGroup label="Business owner">
          <Select
            options={CLIENT_EMPLOYEE_OPTIONS}
            value={businessOwnerId ?? ""}
            onChange={createHandleChange("businessOwnerId")}
          />
        </FieldGroup>
      )}

      <FieldGroup label="Visibility">
        <Select
          options={isVisibleFilterOptions}
          value={isVisibleFilter ?? "visible"}
          onChange={(value) =>
            setValue(
              "isVisibleFilter",
              value as ClientApplicationFilterFormValues["isVisibleFilter"],
              { shouldDirty: true },
            )
          }
        />
      </FieldGroup>

      {/* Hidden submit button to enable Enter key submission */}
      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});
